/**
 * The sun's own time, and what it does to the room.
 *
 * Two jobs that belong together because they are the same question — "how light
 * is it outside right now?" — asked twice. `localSolarHour`/`localCivilDate`
 * turn an instant into the hour and the day the solar maths eats, and
 * `autoSceneLighting` turns the sun's altitude into the exposure and the
 * interior fill the scene should be wearing at that hour.
 *
 * The instant is whatever the caller trusts: `useBimScene` feeds it the
 * server-synced clock (`services/clockSync`), which is what makes the scene's
 * sun agree with the hub's instead of with whichever phone is holding it.
 *
 * A brightness sensor, when one arrives, is a third input to the same rule:
 * `autoSceneLighting` already takes an optional measured brightness (0–1) that
 * overrides the sun's own estimate, so a lux reading can drive the room without
 * changing anything below it. (The hub's weather payload carries
 * `local.illuminance` for exactly this.)
 *
 * Pure: no clock, no DOM, no store. Unit-tested.
 */

/** Zero-padded `YYYY-MM-DD` for an instant, in the machine's own zone. */
export function localCivilDate(epochMs: number): string {
  const at = new Date(epochMs);
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${at.getFullYear()}-${month}-${day}`;
}

/**
 * The hour of the local day, with the minutes as a fraction — `13.75` at 13:45.
 *
 * The scene's `timeOfDay` is decimal hours (the slider runs 0–24 in 15-minute
 * steps), and the solar position wants a solar time in the same units, so this
 * is the one conversion between a clock and the sun.
 */
export function localSolarHour(epochMs: number): number {
  const at = new Date(epochMs);
  return at.getHours() + at.getMinutes() / 60;
}

/**
 * The look the sliders were tuned on: what a midday sun gets. Auto lighting
 * lands exactly here in full day, so switching it on changes nothing about noon
 * and only picks the room up as the sun drops.
 */
export const AUTO_LIGHT_NEUTRAL = { exposure: 1, ambientBoost: 1 } as const;

/**
 * What the rule hands the scene once the sun is gone — the other end of the
 * curve.
 *
 * Measured against the real scene, not guessed. The first pass at 1.35 / 2.2
 * made a 22:00 scene render *brighter than noon*: luminance 173 with 40% of the
 * house's pixels blown past 180, against noon's 139 and 6%. That is because the
 * fill is the uniform `RoomEnvironment` every interior surface picks up, and
 * `scene.environmentIntensity` is the same `0.3 × fill` at noon and at midnight —
 * so the night was never dark to begin with (with no lift at all the house reads
 * 133 against noon's 139). Lifting it more was the wrong direction.
 *
 * Swept downward at 22:00, the house centre runs 133 (fill 1.00) → 119 (0.60) →
 * 112 (0.45) → 107 (0.35) → 101 (0.25, the first setting that starts to crush).
 * This pair sits in the readable middle: the night lands clearly under noon —
 * a 22:00 frame behaves like night at last — while nothing in the room goes to
 * black (0% of the house under 40 luminance, same as noon).
 *
 * The small exposure lift is the pair's own counterweight: it keeps the darker
 * interior from turning murky without re-lifting the whole frame the way the
 * first pass did.
 */
export const AUTO_LIGHT_NIGHT = { exposure: 1.05, ambientBoost: 0.55 } as const;

/**
 * How dark the hour is, 0 (full day) to 1 (night). Civil twilight is the pivot:
 * the sun is at −6° and the sky's own light is gone, so below it nothing more
 * changes and above it the day has begun. Mirrors the scene's own twilight band
 * (`useBimScene` calls −12°…0° "pre-dawn" and 0°…15° "twilight").
 */
export function darknessAt(altitudeDeg: number): number {
  const twilight = -6;
  const fullDay = 30;
  const t = (altitudeDeg - twilight) / (fullDay - twilight);
  return clamp(1 - t, 0, 1);
}

/** What the auto rule hands the scene. */
export interface AutoLighting {
  exposure: number;
  /** The "Interior fill" slider's value — the RoomEnvironment's own strength. */
  ambientBoost: number;
}

/**
 * Exposure and interior fill for a sun altitude.
 *
 * Both move with the light, and — measured on the scene — the fill moves DOWN as
 * the sun goes: it is the uniform `RoomEnvironment` fill every interior surface
 * picks up, and holding it level through the night is what made a 22:00 room read
 * as noon (see `AUTO_LIGHT_NIGHT`). The exposure takes up a little of what the
 * fill gives away, so the room darkens with the hour without going murky.
 *
 * `brightness` (0–1) overrides the sun's own estimate when something has
 * actually measured the light — a lux sensor, or a test.
 *
 * Values are rounded to the sliders' own step (0.05) so what the panel shows is
 * what the scene is doing, and a slider is never left between its own steps.
 */
export function autoSceneLighting(altitudeDeg: number, brightness?: number | null): AutoLighting {
  const dark = brightness === null || brightness === undefined ? darknessAt(altitudeDeg) : clamp(1 - brightness, 0, 1);
  return {
    exposure: snap(lerp(AUTO_LIGHT_NEUTRAL.exposure, AUTO_LIGHT_NIGHT.exposure, dark)),
    ambientBoost: snap(lerp(AUTO_LIGHT_NEUTRAL.ambientBoost, AUTO_LIGHT_NIGHT.ambientBoost, dark)),
  };
}

/** The sliders' own step — a value that cannot be shown is a lying value. */
const SLIDER_STEP = 0.05;

function snap(value: number): number {
  return Math.round(value / SLIDER_STEP) * SLIDER_STEP;
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
